import { AlertTriangle, History, MapPinOff } from 'lucide-react';
import React from 'react';

import type { WebsiteBreakdownResponse, WebsiteHealthResponse } from '@utazzo/shared';

import { relativeTime } from '@/features/dashboard/dashboard-utils';
import { WEBSITE_SURFACE_META } from '@/features/dashboard/website-surface-meta';
import { Card, SectionHeading } from '@/features/website-dashboard/components/ui';
import { sellsWithoutLivePage, statusLabel, statusTone } from '@/features/website-dashboard/lib';

/**
 * Three stacked worklist cards. "Needs attention" and "Recently edited" map
 * one-to-one onto the health aggregate. The third card ("Selling with no live
 * page") replaces the design's approval-queue card, which the two endpoints do
 * not back - instead it surfaces a real, actionable gap from the breakdown.
 */

const VISIBLE_ROWS = 5;

const CardHeader = ({
  icon,
  iconBg,
  iconFg,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  iconBg: string;
  iconFg: string;
  title: string;
  subtitle: string;
}): React.JSX.Element => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '14px 16px',
      borderBottom: '1px solid #eef0f0',
    }}
  >
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: iconBg,
        color: iconFg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </span>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
      <div style={{ fontSize: 12, color: '#6b7075' }}>{subtitle}</div>
    </div>
  </div>
);

const Row = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 16px',
      borderBottom: '1px solid #eef0f0',
      cursor: 'pointer',
    }}
  >
    {children}
    <span style={{ color: '#c5c9cb' }}>›</span>
  </div>
);

const TitleType = ({ title, type }: { title: string; type: string }): React.JSX.Element => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
    <span
      style={{
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {title}
    </span>
    <span style={{ fontSize: 11, color: '#8a9095', flexShrink: 0 }}>{type}</span>
  </div>
);

const Empty = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 12, color: '#8a9095' }}>
    {children}
  </div>
);

interface Props {
  health: WebsiteHealthResponse;
  breakdown: WebsiteBreakdownResponse | undefined;
}

export const Worklist = ({ health, breakdown }: Props): React.JSX.Element => {
  const attention = health.incompleteItems;
  const attentionLive = health.incompleteCounts.reduce((s, c) => s + c.live, 0);
  const attentionDraft = health.incompleteCounts.reduce((s, c) => s + c.draft, 0);

  const recent = health.recentlyEdited;

  const sellingNoPage =
    breakdown?.destinations
      .filter((d) => d.destinationId !== null && sellsWithoutLivePage(d))
      .sort((a, b) => b.packages.live - a.packages.live) ?? [];
  const sellingShown = sellingNoPage.slice(0, VISIBLE_ROWS);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionHeading title="Worklist" />
      <div className="wd-work" style={{ display: 'grid', gap: 12, alignItems: 'start' }}>
        {/* Needs attention - live rows missing fields */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            icon={<AlertTriangle style={{ width: 18, height: 18 }} />}
            iconBg="#fef3c7"
            iconFg="#b45309"
            title="Needs attention"
            subtitle={
              attentionLive > 0
                ? `${attentionLive} live ${attentionLive === 1 ? 'page is' : 'pages are'} missing information${
                    attentionDraft > 0 ? ` · ${attentionDraft} drafts too` : ''
                  }`
                : 'Every live page has its fields filled in.'
            }
          />
          {attention.length === 0 ? (
            <Empty>Nothing to fix right now.</Empty>
          ) : (
            // Show every item that needs attention; the card keeps a fixed height
            // and the full list scrolls inside it.
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {attention.map((item) => (
                <Row
                  key={`${item.surface}:${item.id}`}
                  onClick={() =>
                    window.open(
                      WEBSITE_SURFACE_META[item.surface].editPath(item.id),
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <TitleType title={item.title} type={WEBSITE_SURFACE_META[item.surface].label} />
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 4,
                        marginTop: 4,
                      }}
                    >
                      {item.missing.slice(0, 3).map((m) => (
                        <span
                          key={m}
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#92400e',
                            background: '#fef3c7',
                            borderRadius: 4,
                            padding: '2px 6px',
                          }}
                        >
                          {m}
                        </span>
                      ))}
                      <span style={{ fontSize: 10, color: '#8a9095' }}>
                        {item.missing.length > 3 ? `+${item.missing.length - 3} more · ` : ''}
                        {relativeTime(item.updatedAt)}
                      </span>
                    </div>
                  </div>
                </Row>
              ))}
            </div>
          )}
        </Card>

        {/* Selling with no live page - replaces the approval-queue card */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            icon={<MapPinOff style={{ width: 18, height: 18 }} />}
            iconBg="#fee2e2"
            iconFg="#b91c1c"
            title="Selling with no live page"
            subtitle="Destinations with published packages but no live destination page."
          />
          {breakdown === undefined ? (
            <Empty>Loading…</Empty>
          ) : sellingShown.length === 0 ? (
            <Empty>Every selling destination has a live page.</Empty>
          ) : (
            sellingShown.map((row) => (
              <Row
                key={row.destinationId ?? 'none'}
                onClick={() => window.open('/destination-pages', '_blank', 'noopener,noreferrer')}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <TitleType
                    title={row.name}
                    type={row.isInternational ? 'International' : 'Domestic'}
                  />
                  <div style={{ marginTop: 4, fontSize: 11, color: '#6b7075' }}>
                    {row.packages.live} live {row.packages.live === 1 ? 'package' : 'packages'}{' '}
                    selling
                  </div>
                </div>
              </Row>
            ))
          )}
          {sellingNoPage.length > sellingShown.length && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px' }}>
              <a
                href="/destination-pages"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 12, fontWeight: 700 }}
              >
                {sellingNoPage.length - sellingShown.length} more →
              </a>
            </div>
          )}
        </Card>

        {/* Recently edited - cross-surface feed */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            icon={<History style={{ width: 18, height: 18 }} />}
            iconBg="#e0f2fe"
            iconFg="#0369a1"
            title="Recently edited"
            subtitle="Latest changes across every website surface."
          />
          {recent.length === 0 ? (
            <Empty>No edits yet.</Empty>
          ) : (
            recent.map((row) => {
              const tone = statusTone(row.status);
              return (
                <Row
                  key={`${row.surface}:${row.id}`}
                  onClick={() =>
                    window.open(
                      WEBSITE_SURFACE_META[row.surface].editPath(row.id),
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <TitleType title={row.title} type={WEBSITE_SURFACE_META[row.surface].label} />
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginTop: 4,
                        fontSize: 11,
                        color: '#6b7075',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          borderRadius: 4,
                          padding: '2px 6px',
                          background: tone.bg,
                          color: tone.fg,
                        }}
                      >
                        {statusLabel(row.status)}
                      </span>
                      <span>
                        {relativeTime(row.updatedAt)}
                        {row.updatedByName ? ` · ${row.updatedByName}` : ''}
                      </span>
                    </div>
                  </div>
                </Row>
              );
            })
          )}
        </Card>
      </div>
    </section>
  );
};
